import React, { useEffect } from "react";
import {
    Typography,
    Grid,
    Avatar,
    Paper,
    makeStyles,
    Card,
    Button,
    IconButton,
    CardContent,
} from "@material-ui/core";
import ShareIcon from "@material-ui/icons/Share";

import MainScreen from "./templates/MainScreen";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import routes from "../../utils/routes.json";
import api from "../../utils/api";
import { getendGameStats } from "../../viewmodel/providers/endGameStats";
import { useEndGameStats } from "../../viewmodel/hooks/getEndGameStats";
import UserInfo from "../../model/userInfo";

import { SoundEffectContext } from "../../viewmodel/providers/soundEffect-context";

import Sound from "react-sound";

//import victory from "./audio/Players_Victory.mp3";

const useStyles = makeStyles((theme) => ({
    avatar: {
        width: "110px",
        height: "110px",
    },
    avatarsGrid: {
        maxWidth: "500px",
    },
    centerHorizontal: {
        marginLeft: "auto",
        marginRight: "auto",
    },
    centerText: {
        textAlign: "center",
    },
    fullHeight: {
        height: "100%",
    },
    fullWidth: {
        width: "100%",
    },
    blackButton: {
        backgroundColor: theme.palette.primary.main,
        "&:hover": {
            background: theme.palette.primary.main,
        },
        width: "40%",
        maxWidth: "300px",
        borderRadius: "4px",
    },
    share: {
        width: "1.5em",
        height: "1.5em",
    },
    noPadding: {
        padding: "0",
    },
    middleGrid: {
        maxWidth: "800px",
    },
    lowerGrid: {
        maxWidth: "600px",
    },
    blackText: {
        color: "black",
    },
    goodText: {
        color: "green",
    },
    badText: {
        color: "red",
    },
    shareButton: {
        margin: "0 auto",
    },
    stats: {
        ["@media (min-width: 780px)"]: {
            transform: "scale(1.4)",
        },
    },
    tie: {
        textTransform: "uppercase",
        fontWeight: "bold",
        fontSize: "400%",
        paddingTop: "100px",
    },
    victory: {
        color: "green",
        textTransform: "uppercase",
        fontWeight: "bold",
        fontSize: "400%",
        paddingTop: "100px",
    },
    loss: {
        color: "red",
        textTransform: "uppercase",
        fontWeight: "bold",
        fontSize: "400%",
        paddingTop: "100px",
    },
}));

type GameResultProps = {
    winner: string | undefined;
    gameId: string;
    user: UserInfo;
    opponent: UserInfo;
    oponnentId: string | undefined,
    categoryIds: string[];
}

export default function GameResult({ winner, gameId, user, opponent, oponnentId, categoryIds }: GameResultProps): JSX.Element {
    const { t } = useTranslation();
    const classes = useStyles();

    const soundEffect = React.useContext(SoundEffectContext).soundEffect;

    let isVictory = false;

    if (winner === "game-result.win") {
        isVictory = true;
    }

    const sendRematch = () => {
        api.put("game/rematch", {
            user: oponnentId,
            message: JSON.stringify({ message: "Rematch", "categoryIds": categoryIds.join(",") }),
        });
    };
    let colorClass = classes.tie;
    if (winner === "game-result.lost") colorClass = classes.loss;
    else if (winner === "game-result.win") colorClass = classes.victory;

    const [endGameStats, setEndGameStats] = useEndGameStats();
    useEffect(() => {
        getendGameStats(gameId, setEndGameStats);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const experiencePoints = Math.round(Number(endGameStats.currentExperiencePoints));
    const gainedExperiencePoints = Math.round(Number(endGameStats.changeInExperiencePoints));

    let textColor = classes.blackText;
    if (endGameStats.changeInRating === "1")
        textColor = classes.goodText;
    else if (endGameStats.changeInRating === "-1")
        textColor = classes.badText;

    return (
        <MainScreen lower={false} back={true} backPath={routes.menu}>
            <Grid container direction="column" justify="space-around" classes={{ root: classes.fullHeight }}>
                <Card elevation={0} classes={{ root: classes.fullWidth }}>
                    <CardContent>
                        <Grid
                            container direction="row" justify="space-around"
                            className={`${classes.centerHorizontal} ${classes.avatarsGrid}`}
                        >
                            <Avatar
                                alt={user.name}
                                src={user.image}
                                className={classes.avatar}
                            />
                            <Avatar
                                alt={opponent.name}
                                src={opponent.image}
                                className={classes.avatar}
                            />
                        </Grid>
                        <Grid container direction="row" justify="space-evenly" classes={{ root: classes.centerHorizontal }}>
                            <Typography variant="h3" className={`${colorClass}`}>
                                {t(winner || "")}
                                {/*isVictory ?
                                    <Sound
                                        url={victory}
                                        playStatus={soundEffect ? "PLAYING" : "STOPPED"}
                                        volume={7}
                                    />
    : null */}
                            </Typography>
                        </Grid>
                    </CardContent>
                </Card>
                <Grid container direction="row" justify="space-evenly" className={`${classes.centerHorizontal} ${classes.middleGrid}`}>
                    <Paper elevation={0} className={`${classes.stats}`}>
                        <Avatar className={`${classes.blackText} ${classes.centerHorizontal}`}>
                            {gainedExperiencePoints}
                        </Avatar>
                        <Typography variant="body2" classes={{ root: classes.centerText }}>
                            {t("game-result.gainedXp")}
                        </Typography>
                    </Paper>
                    <Paper elevation={0} className={`${classes.stats}`}>
                        <Avatar className={`${classes.blackText} ${classes.centerHorizontal}`}>
                            {experiencePoints}
                        </Avatar>
                        <Typography variant="body2" classes={{ root: classes.centerText }}>
                            {t("game-result.xp")}
                        </Typography>
                    </Paper>
                    <Paper elevation={0} className={`${classes.stats}`}>
                        <Avatar className={`${textColor} ${classes.centerHorizontal}`}>
                            {endGameStats.currentRating}
                        </Avatar>
                        <Typography variant="body2" classes={{ root: classes.centerText }}>
                            {t("game-result.rank")}
                        </Typography>
                    </Paper>
                </Grid>
                <IconButton aria-label="search" className={`${classes.noPadding} ${classes.shareButton}`}>
                    <ShareIcon classes={{ root: classes.share }} />
                </IconButton>
                <Grid container direction="row" justify="space-between" className={`${classes.centerHorizontal} ${classes.lowerGrid}`}>
                    <Link
                        to={{
                            pathname: routes.replay,
                            state: {
                                oponnentId: oponnentId,
                                confirm: false,
                                categoryIds: categoryIds,
                            },
                        }}
                        className={`${classes.blackButton}`}
                    >
                        <Button color="primary" classes={{ root: classes.fullWidth }} onClick={() => sendRematch()}>
                            <Typography variant="h5" color="secondary" classes={{ root: classes.centerHorizontal }}>
                                {t("game-result.rematch")}
                            </Typography>
                        </Button>
                    </Link>
                    <Link to={routes.categories} className={`${classes.blackButton}`}>
                        <Button color="primary" classes={{ root: classes.fullWidth }}>
                            <Typography variant="h5" color="secondary" classes={{ root: classes.centerHorizontal }}>
                                {t("game-result.newGame")}
                            </Typography>
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </MainScreen>
    );
}
